import { Box, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Section } from 'src/components/section';
import { Select } from 'src/components/select';
import { Currency } from 'src/shared/enum/Currency';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { InstalmentType } from 'src/shared/enum/InstalmentType';
import { Term } from 'src/shared/types/booking-types';
import { getTheme } from 'src/utils/getTheme';
import { formatDate } from 'src/utils/format-date';
import { getCurrencySymbol } from 'src/utils/get-currency-symbol';
import { createLabelFromInstalmentType } from 'src/utils/instalment-type-to-name-convert';
import { getStorage } from 'src/utils/local-storage-versioning';
import { BookingDetailsSkeleton } from 'src/features/booking-summary/booking-details/booking-details-skeleton';

import { amendBookingDetailsStyles } from '../styles';
import { AmendBookingDetailsViewProps } from '../types';

export const AmendBookingDetailsView: FC<AmendBookingDetailsViewProps> = ({
    roomData,
    terms,
    userChoices,
    handleTermChange,
    handleInstalmentChange,
    loading,
}) => {
    const { t } = useTranslation();
    const { numberOfStudents, term: selectedTerm, instalmentType } = userChoices;
    const { isHosTheme } = getTheme();
    const storage = getStorage();
    const { paymentPlanId = null } = storage.redirectState;

    const termName = selectedTerm && selectedTerm.name;
    const monthlyPrice = roomData && roomData.monthly_price;
    const PRICE_PER_WEEK_LABEL = monthlyPrice
        ? t('booking_summary_per_month_payment')
        : t('booking_summary_per_week_payment');

    useEffect(() => {
        if (terms && terms.length > 0 && !selectedTerm) {
            handleTermChange(terms[0]);
        }

        if (selectedTerm) {
            const selectedTermDetails = terms.find((term) => term.name === selectedTerm.name);

            if (selectedTermDetails) {
                const instalmentsExist =
                    selectedTermDetails.instalments && selectedTermDetails.instalments.length > 0;

                if (instalmentsExist && !instalmentType) {
                    handleInstalmentChange(
                        selectedTermDetails.instalments[0].name as InstalmentType,
                        selectedTermDetails.instalments[0].id
                    );
                } else if (!instalmentsExist) {
                    handleInstalmentChange(null, null);
                }
            } else {
                handleTermChange(terms[0]);
                handleInstalmentChange(
                    terms[0].instalments[0].name as InstalmentType,
                    terms[0].instalments[0].id
                );
            }
        }
    }, []);

    if (loading) return <BookingDetailsSkeleton />;

    const getTermTitle = (count: number) => {
        if (monthlyPrice && !paymentPlanId) {
            return t('booking_summary_months_count', {
                count,
            });
        }
        return t('booking_summary_weeks_count', {
            count,
        });
    };

    const getInstalmentOptions = () => {
        if (!terms) throw new Error(ErrorMessage.NO_TERMS_PROVIDED);

        // Fetch the currency symbol using the CurrencySymbol mapping
        const currencySymbol = getCurrencySymbol(roomData.currency as Currency);

        const selectedTermDetails = terms.find((term) => term.name === termName);
        if (!selectedTermDetails || !selectedTermDetails.instalments) return [];

        return selectedTermDetails.instalments.map((instalment) => {
            const selectedAmount = instalment.amounts.find(
                (amt) => amt.students === numberOfStudents
            );
            const price = selectedAmount ? new Decimal(selectedAmount.price) : new Decimal(0);

            const instalmentLabel = createLabelFromInstalmentType(instalment.name, t);
            const pricePwLabel = PRICE_PER_WEEK_LABEL;

            return {
                id: instalment.id,
                value: instalment.name,
                title: `${currencySymbol}${price.toFixed(2)}${pricePwLabel}`,
                subtitle: instalmentLabel,
            };
        });
    };

    return (
        <Section header={t('booking_summary_booking_details_title')} withHeaderStipe={!isHosTheme}>
            <Box css={amendBookingDetailsStyles.wrapper}>
                <Box css={amendBookingDetailsStyles.bookingLengthWrapper}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_length_subtitle')}
                    </Typography>
                    <Box css={amendBookingDetailsStyles.bookingLength}>
                        {terms.map((term: Term) => (
                            <Select
                                key={term.name}
                                value={term.name}
                                isSelected={termName === term.name}
                                onClick={() => handleTermChange(term)}
                                title={getTermTitle(term.duration)}
                                subtitle={`${formatDate(term.startDate)} - ${formatDate(term.endDate)}`}
                            />
                        ))}
                    </Box>
                </Box>
                <Box css={amendBookingDetailsStyles.instalmentPlanWrapper}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_instalment_plan_subtitle')}
                    </Typography>
                    <Box css={amendBookingDetailsStyles.instalmentPlan}>
                        {getInstalmentOptions().map(({ value, title, subtitle, id }) => (
                            <Select
                                key={value}
                                value={value}
                                isSelected={instalmentType === value}
                                onClick={() => handleInstalmentChange(value, id)}
                                title={title}
                                subtitle={subtitle}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Section>
    );
};
